import { ProjectForCurrentUser, UserProfile, Project } from "./types";
import security from "./SecurityService";
import appClient, { ResultApi } from "./AppClient";
import { Utils } from "../misc/Utils";
import { endpoints } from "../../../config";

export default class ProjectsService {
    async getProjectsForCurrentUser(): Promise<
        ResultApi<ProjectForCurrentUser[]>
    > {
        const url = `${endpoints.tasksUrl}project/current-user`;
        return appClient.get<ProjectForCurrentUser[]>(url);
    }

    async getAllProjects(): Promise<ResultApi<Project[]>> {
        const url = `${endpoints.tasksUrl}project`;
        return appClient.get<Project[]>(url);
    }

    async createUpdateProject(
        id: string | null,
        name: string,
        owner: string | undefined,
        enrichmentEnabled: boolean,
    ): Promise<ResultApi<string>> {
        const request = {
            id,
            name,
            owner: owner ?? null,
            enrichmentEnabled,
        };
        const url = `${endpoints.tasksUrl}project`;
        return appClient.post<string>(url, request);
    }

    handleDownload(id: string, fileType: "pdf" | "apkg") {
        const path = encodeURIComponent(id);
        security.invoke((token) => {
            const t = "?access_token=" + encodeURIComponent(token);
            let url = "";
            if (fileType === "pdf") {
                url = `${process.env.REACT_APP_MANAGE_URL}document/${path}${t}`;
            } else {
                url = `${process.env.REACT_APP_MANAGE_URL}file/${fileType}/${path}${t}`;
            }
            window.open(url);
            return Promise.resolve();
        });
    }

    getPackageAsync(packageId: string) {
        const url =
            process.env.REACT_APP_MANAGE_URL +
            `packages/${packageId}?includeFields=false`;
        return appClient.get(url);
    }

    uploadPackage(formData: FormData, projectId: string) {
        const url =
            process.env.REACT_APP_MANAGE_URL +
            `projects/${projectId}/client/upload`;
        return appClient.post(url, formData);
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    downloadProject(data: any, fileName: string) {
        const blob = new Blob([data], { type: "application/json" });
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = fileName;
        link.click();
    }

    // async exportProject(projectId: string) {
    //     const url = endpoints.tasksUrl + `admin/project/export/${projectId}`;
    //     window.open(url);
    //     return Promise.resolve();
    // }

    async exportProject(projectId: string) {
        const mapForm = document.createElement("form");
        mapForm.setAttribute("id", "projectExportPostForm");
        mapForm.target = "_blank";
        mapForm.method = "POST";

        await security.invoke((token) => {
            const t = "?access_token=" + encodeURIComponent(token);
            const url = endpoints.tasksUrl + `project/export/${projectId}${t}`;
            Utils.downloadFile(url, mapForm, "projectExportPostForm");
            return Promise.resolve();
        });
    }

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    async importProject(project: any): Promise<ResultApi<string>> {
        const url = endpoints.tasksUrl + "project/import";
        return appClient.post(url, project);
    }

    async deleteProject(projectId: string) {
        const url = endpoints.tasksUrl + `project/${projectId}`;
        return appClient.delete(url);
    }

    uploadPackageSet(formData: FormData, projectId: string) {
        const url = endpoints.tasksUrl + `projects/${projectId}/upload-set`;
        return appClient.post(url, formData);
    }

    async getAllUsers() {
        const url = `${endpoints.tasksUrl}userprofile`;
        const resp = await appClient.get<UserProfile[]>(url);
        return resp.unwrapOr(Array<UserProfile>());
    }

    async getUsersInProject(projectId: string) {
        const url =
            endpoints.tasksUrl +
            `users/project/${projectId}?includeRoles=false`;
        const resp = await appClient.get<UserProfile[]>(url);
        return resp.unwrapOr(Array<UserProfile>());
    }

    async sendPackageViaEmail(id: string, emails: string[], message?: string) {
        const request = {
            emails,
            message,
        };

        const url = endpoints.tasksUrl + `packages/${id}/send-by-email`;
        return appClient.post(url, request);
    }
}
